import moment from 'moment';
import { createContext, useContext, useEffect, useState } from 'react';

const DEFAULT_NUMBER_OF_DAYS = 5;
const DEFAULT_FORMAT_DATE = 'YYYY-MM-DD';

const getMomentDate = (date: string): moment.Moment => {
  const parsedDate = moment(date, [moment.ISO_8601, DEFAULT_FORMAT_DATE], true);
  return parsedDate.isValid() ? parsedDate : moment();
};

const formatDateForRequest = (date: moment.Moment): string => date.clone().locale('en').format(DEFAULT_FORMAT_DATE);

const getMondayBasedOnDate = (date: moment.Moment): moment.Moment => date.add(1, 'day').startOf('isoWeek');

export type AvailabilityCalendarContextType = {
  selectedDate?: string;
  endDate?: string;
  startDateForCurrentWeek?: string;
  numberOfDays?: number;
  canGoToPreviousWeek?: boolean;
  canGoToNextWeek?: boolean;
  setSelectedDate?: (date: string) => void;
  setStartDateForCurrentWeek?: (startDate: string) => void;
  setPreviousWeek?: () => void;
  setNextWeek?: () => void;
  setToday?: () => void;
  getRange?: () => string[];
};

export const AvailabilityCalendarContext = createContext<AvailabilityCalendarContextType>({});

export const useAvailabilityCalendarContext = () => useContext(AvailabilityCalendarContext);

export const AvailabilityCalendarContextProvider = (props: {
  children: React.ReactNode;
  value: Partial<AvailabilityCalendarContextType>;
}) => {
  const { children, value } = props;
  const numberOfDays = value?.numberOfDays ?? DEFAULT_NUMBER_OF_DAYS;
  const selectedDate = value?.selectedDate || '';
  const endDate = value?.endDate || '';
  const setSelectedDate = value?.setSelectedDate || (() => {});

  const [startDateForCurrentWeek, setStartDateForCurrentWeek] = useState<string>(value?.startDateForCurrentWeek || '');
  const [canGoToPreviousWeek, setCanGoToPreviousWeek] = useState<boolean>(true);
  const [canGoToNextWeek, setCanGoToNextWeek] = useState<boolean>(true);

  const updateStartDateForCurrentWeek = (modifier: (date: moment.Moment) => moment.Moment) => {
    setStartDateForCurrentWeek((prev) => (
      formatDateForRequest(getMondayBasedOnDate(modifier(getMomentDate(prev))))
    ));
  };

  const setToday = () => {
    setStartDateForCurrentWeek(
      formatDateForRequest(getMondayBasedOnDate(moment())),
    );
  };

  const setPreviousWeek = () => {
    updateStartDateForCurrentWeek((date) => date.subtract(1, 'week'));
  };

  const setNextWeek = () => {
    updateStartDateForCurrentWeek((date) => date.add(1, 'week'));
  };

  const getRange = () => {
    const start = getMomentDate(startDateForCurrentWeek);
    const monday = getMondayBasedOnDate(start);
    const range = Array.from(
      { length: numberOfDays },
      (_, i) => formatDateForRequest(monday.clone().add(i, 'days')),
    );
    return range;
  };

  useEffect(() => {
    const start = getMomentDate(startDateForCurrentWeek);
    const monday = getMondayBasedOnDate(start);
    const range = Array.from(
      { length: numberOfDays },
      (_, i) => formatDateForRequest(monday.clone().add(i, 'days')),
    );
    const today = formatDateForRequest(moment());
    const endMoment = moment(endDate);
    const nextWeekStart = getMomentDate(startDateForCurrentWeek).add(1, 'week');
    setCanGoToPreviousWeek(!range.includes(today));
    setCanGoToNextWeek(!endMoment.isValid() || nextWeekStart.isBefore(endMoment));
  }, [startDateForCurrentWeek, endDate, numberOfDays]);

  return (
    <AvailabilityCalendarContext.Provider
      value={{
        selectedDate,
        endDate,
        startDateForCurrentWeek,
        numberOfDays,
        canGoToPreviousWeek,
        canGoToNextWeek,
        setSelectedDate,
        setStartDateForCurrentWeek,
        setToday,
        setPreviousWeek,
        setNextWeek,
        getRange,
      }}
    >
      {children}
    </AvailabilityCalendarContext.Provider>
  );
};
